// Simple line-based LCS diff for admin trace per-attempt comparisons.
//
// Why hand-rolled: the V2 admin trace UI deliberately avoids new heavy
// dependencies (see package.json — react, react-query, react-router only).
// Attempt-vs-attempt diffs are small (retry_feedback + corrected JSON),
// so a 50-line LCS over O(n*m) is fine. If diff size ever grows past a
// few hundred lines per side, swap to the `diff` npm package.
//
// Output: an array of segments where kind is "eq" (unchanged), "add"
// (right-only), or "del" (left-only). Renderers map kinds → colors.

export type DiffSegmentKind = "eq" | "add" | "del";

export interface DiffSegment {
  kind: DiffSegmentKind;
  text: string;
}

// diffLines computes a line-level diff between two strings via LCS.
// Returns segments in display order. Each segment is one line; the renderer
// joins them.
export function diffLines(left: string, right: string): DiffSegment[] {
  const a = left.split("\n");
  const b = right.split("\n");
  const n = a.length;
  const m = b.length;
  // LCS table (n+1 by m+1).
  const dp: number[][] = Array.from({ length: n + 1 }, () =>
    new Array<number>(m + 1).fill(0),
  );
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      if (a[i] === b[j]) {
        dp[i][j] = dp[i + 1][j + 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1]);
      }
    }
  }
  const out: DiffSegment[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      out.push({ kind: "eq", text: a[i] });
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      out.push({ kind: "del", text: a[i] });
      i++;
    } else {
      out.push({ kind: "add", text: b[j] });
      j++;
    }
  }
  while (i < n) {
    out.push({ kind: "del", text: a[i] });
    i++;
  }
  while (j < m) {
    out.push({ kind: "add", text: b[j] });
    j++;
  }
  return out;
}
